/**
 * High-performance account information store
 */

import { create } from 'zustand';
import type {
  Account,
  AccountResponse,
  AccountUpdate,
  AccountInformationState,
  AccountInformationActions,
} from '../types';

interface AccountInformationStore extends AccountInformationState, AccountInformationActions {}

const initialState: AccountInformationState = {
  account: null,
  loadingStatus: 'idle',
  errorMessage: null,
  lastUpdated: null,
};

const createAccountFromUpdate = (update: AccountUpdate): Account => ({
  login: update.login,
  type: '',
  userName: '',
  tradeFlags: update.tradeFlags ?? 0,
  country: '',
  city: '',
  state: '',
  zipCode: '',
  userAddress: '',
  phone: '',
  email: '',
  balance: update.balance,
  credit: update.credit,
  blocked: update.blocked ?? 0,
  leverage: update.leverage ?? 0,
  currency: update.currency,
  companyName: '',
  serverName: '',
  host: '',
  accountMethod: '',
  marginMode: '',
  serverBuild: update.serverBuild ?? 0,
  serverTimeZoneMinutes: 0,
  symbolBaseInfo: update.symbolBaseInfo ?? null,
  equity: update.equity,
  margin: update.margin,
  freeMargin: update.freeMargin,
  marginLevel: update.marginLevel,
  profit: update.profit,
  timestamp: update.timestamp,
  isInvestor: update.isInvestor === true,
  marginFromServer: update.marginFromServer === true,
});

const syncProfitConversionSymbolsForAccount = (accountCurrency?: string | null) => {
  if (!accountCurrency) return;

  Promise.all([
    import("../../opened-trades/stores/useOpenedTradesStore"),
    import("../../opened-trades/utils"),
    import("../../symbols/stores/useSymbolsStore"),
    import("../../subscribed-symbols/stores/useSubscribedSymbolsStore"),
    import("../../../lib/quotes-watchlist/store"),
  ])
    .then(
      ([
        { useOpenedTradesStore },
        { getRequiredProfitConversionSymbols, isMarketTrade },
        { useSymbolsStore },
        { useSubscribedSymbolsStore },
        { useQuotesWatchlistStore },
      ]) => {
        const trades = useOpenedTradesStore.getState().allTrades;
        const symbolsStore = useSymbolsStore.getState();
        const subscribedStore = useSubscribedSymbolsStore.getState();
        const watchlistStore = useQuotesWatchlistStore.getState();
        const requiredSymbols = new Set<string>();

        trades.forEach((trade) => {
          if (!isMarketTrade(trade)) return;

          const tradeSymbol = symbolsStore.getSymbolByCurrency(trade.symbol);
          if (!tradeSymbol) return;

          getRequiredProfitConversionSymbols(
            tradeSymbol.profitCurrency,
            accountCurrency,
            symbolsStore.byCurrency
          ).forEach((symbolName) => requiredSymbols.add(symbolName));
        });

        requiredSymbols.forEach((symbolName) => {
          const conversionSymbol = symbolsStore.getSymbolByCurrency(symbolName);
          if (conversionSymbol) {
            watchlistStore.addToWatchlist(conversionSymbol);
            if (!subscribedStore.isSubscribed(symbolName)) {
              subscribedStore.subscribe(conversionSymbol);
            }
          }
        });
      }
    )
    .catch((error) => {
      console.error("[AccountInformation] Failed to sync conversion symbols:", error);
    });
};

export const useAccountInformationStore = create<AccountInformationStore>((set, get) => {
  // Create stable function references
  const getAccount = () => get().account;

  return {
    ...initialState,

    processAccountData: (data: AccountResponse) => {
      set((state) => {
        // Handle single response object
        if (!data.success) {
          return {
            ...state,
            loadingStatus: 'error',
            errorMessage: data.errorMessage,
          };
        }

        return {
          ...state,
          account: {
            ...data.account,
            tradeFlags: data.account.tradeFlags ?? 0,
            blocked: data.account.blocked ?? 0,
            currency: data.account.currency || state.account?.currency || '',
            companyName: data.account.companyName || state.account?.companyName || '',
            serverName: data.account.serverName || state.account?.serverName || '',
            host: data.account.host || state.account?.host || '',
            accountMethod:
              data.account.accountMethod || state.account?.accountMethod || '',
            marginMode: data.account.marginMode || state.account?.marginMode || '',
            serverBuild: data.account.serverBuild ?? state.account?.serverBuild ?? 0,
            serverTimeZoneMinutes:
              data.account.serverTimeZoneMinutes ??
              state.account?.serverTimeZoneMinutes ??
              0,
            symbolBaseInfo:
              data.account.symbolBaseInfo ?? state.account?.symbolBaseInfo ?? null,
            equity:
              data.account.equity ??
              state.account?.equity ??
              data.account.balance + data.account.credit,
            margin: data.account.margin ?? state.account?.margin ?? 0,
            freeMargin:
              data.account.freeMargin ??
              state.account?.freeMargin ??
              data.account.balance + data.account.credit,
            marginLevel: data.account.marginLevel ?? state.account?.marginLevel ?? 0,
            profit: data.account.profit ?? state.account?.profit ?? 0,
            timestamp: state.account?.timestamp ?? null,
            isInvestor: data.account.isInvestor === true,
            marginFromServer: data.account.marginFromServer === true,
          },
          loadingStatus: 'complete',
          errorMessage: null,
          lastUpdated: new Date(),
        };
      });
      syncProfitConversionSymbolsForAccount(data.account?.currency);
    },

    processAccountUpdate: (data: AccountUpdate) => {
      set((state) => {
        const nextAccount = state.account
          ? {
              ...state.account,
              login: data.login,
              balance: data.balance,
              credit: data.credit,
              equity: data.equity,
              margin: data.margin,
              freeMargin: data.freeMargin,
              marginLevel: data.marginLevel,
              profit: data.profit,
              currency: data.currency || state.account.currency,
              blocked: data.blocked ?? state.account.blocked,
              leverage: data.leverage ?? state.account.leverage,
              tradeFlags: data.tradeFlags ?? state.account.tradeFlags,
              serverBuild: data.serverBuild ?? state.account.serverBuild,
              symbolBaseInfo: data.symbolBaseInfo ?? state.account.symbolBaseInfo,
              timestamp: data.timestamp,
              isInvestor: data.isInvestor ?? state.account.isInvestor ?? false,
              marginFromServer: data.marginFromServer ?? state.account.marginFromServer ?? false,
            }
          : createAccountFromUpdate(data);

        return {
          ...state,
          account: nextAccount,
          loadingStatus: 'complete',
          errorMessage: null,
          lastUpdated: data.timestamp ? new Date(data.timestamp) : new Date(),
        };
      });
      syncProfitConversionSymbolsForAccount(data.currency);
    },

    reset: () => set(initialState),

    // Stable function reference
    getAccount,
  };
});
